<template>
  <div role="main" class="flexbox">
    <div class="left-panel" v-bind:style="(hideMaintenanceBanner) ? 'height: 100%' : 'height: 95%'">
      <CorporateBranding></CorporateBranding>
    </div>
    <div class="flexbox container" v-bind:style="(hideMaintenanceBanner) ? 'height: 100%' : 'height: 95%'">
      <div class="flexbox content viewable-height">
        <div class="top-container">
          <CustomDropDown
            v-model="selectedLanguage"
            :options="languages"
            optionKey="Value"
            optionValue="Label"
            name="Language"
            placeHolderValue=""
            :isoptionSelectOnClose="false"
            :initValue="selectedLanguage"
            @update="selectLanguage"
            class="language-dropdown">
          </CustomDropDown>
          <div class="help-box-container">
            <img class="help-box-mobile-icon" src="@/assets/icons/help.svg">
            <HelpBox class="help-box" :supportNumber="supportNumber"/>
          </div>
        </div>
        <div class="branding-container">
            <img :src="getBrandingLogoURL" v-if="hasPrimaryLogo"/>
        </div>
        <PageHeader
          :startDate="conferenceStartDate"
          :duration="conferenceDuration"
          :topic="topic"
          :title="title"
        />
        <slot></slot>
      </div>
      <Footer class="reg-flow-footer"></Footer>
    </div>
     <GenericModal
       class="error-modal"  
       :show="toShowModal" 
       :onClose="toggleGenericModal"
       :message = "errorMsg" 
      ></GenericModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import '@/utils/validationData';
import PageHeader from "@/components/common/PageHeader";
import { GetPreferredLanguage, setBrowserCookie } from "@/utils/helper";
import Constants from '@/utils/constants';
import CustomDropDown from '@/components/common/CustomDropDown/MaterialCustomDropDown.vue';
import GenericModal from "@/components/common/GenericAlertModal";
import CorporateBranding from "@/components/common/CorporateBranding";
import Footer from '@/components/common/Footer/FooterConnectfn.vue';
import HelpBox from '@/components/common/HelpBox';

// Composables
import { useGlobal, useCorporateBranding } from '@/composables';

// Props
const props = defineProps({
  /**
   * If you just want to see the main content
   * this will hide the header footer. Will give
   * a blank screen
   **/
  title: { type: String, default: "" },
  conferenceStartDate: { type: String, default: "" },
  conferenceDuration: { type: String, default: "" },
  topic: { type: String, default: "" },
  supportNumber: { type: String, default: "" }
});

// Setup composables
const { locale } = useI18n();
const {
  hideMaintenanceBanner,
  showMGMjoinLinkErrorModal,
  setShowMGMjoinLinkErrorModal
} = useGlobal();

const {
  hasBrandingLogo,
  getBrandingLogos
} = useCorporateBranding();

// Reactive data
const languages = Constants.SupportedLanguages;
const selectedLanguage = ref('');

// Computed properties
const getBrandingLogoURL = computed(() => {
  var primaryLogo = getBrandingLogos.value.find(l => l.BrandingGroupItem != null && l.BrandingGroupItem.IsPrimary == true);
  if (primaryLogo != null) {
    return primaryLogo.BrandingGroupItem.LogoPath;
  }
  return '';
});

const hasPrimaryLogo = computed(() => {
  return getBrandingLogos.value.findIndex(l => l.BrandingGroupItem != null && l.BrandingGroupItem.IsPrimary == true) !== -1;
});

const errorMsg = computed(() => {
  return showMGMjoinLinkErrorModal.value ? showMGMjoinLinkErrorModal.value.Error : '';
});

const toShowModal = computed(() => {
  return showMGMjoinLinkErrorModal.value !== null;
});

// Methods
const selectLanguage = (data) => {
  locale.value = data.Value;
  setBrowserCookie(Constants.Cookies.LanguagePreference, data.Value, 1000, '/');
};

const toggleGenericModal = () => {
  setShowMGMjoinLinkErrorModal(null);
};

const filteredLang = languages.filter(item => item.Value === GetPreferredLanguage());
selectedLanguage.value = filteredLang.length ? filteredLang[0] : languages[0];

</script>

<style lang="stylus" scoped>
.viewable-height {
  overflow-y auto;
  overflow-x hidden;
  padding: 15px 15px 15px 128px !important;

  @media screen and (max-width: 768px) {
    flex none;
    min-height calc(100% - 50px);
    height auto;
    padding: 15px 35px 15px 35px !important;
  }

  .container-autocomplete {
    width:110px;
    @media screen and (max-width: 1200px) {
      width:40px;
    }    
    padding: 0px;
    float: right;
    :deep(.multiselect) {
      .multiselect__single {
          padding-top: 0px !important;
          color : #43425D;
      }
      .multiselect__single .option__title {
        @media screen and (max-width: 1200px) {
          display: none;
        }
      }
      .multiselect__single .option__image {
        @media screen and (max-width: 1200px) {
          width: 22px;
        }
      }
      .multiselect__tags {
        border: none;
        padding-top: 6px;
        padding-right: 30px;
        @media screen and (max-width: 1200px) {
          padding-right: 0px;
        }
      }
      .multiselect__select {
        width: 30px;
        right: 9px;
        top: -0.25px;
        @media screen and (max-width: 1200px) {
          display: none;
        }
      }
      .multiselect__content-wrapper {
        @media screen and (max-width: 1200px) {
          right: 0px;
          width: 209px;
        }
      }
    }
  }
  .language-dropdown {
    padding-right: 25px;
    @media screen and (max-width: 1200px) {
      padding-right: 0;
      position: relative;
    }
  }
  .help-box {
    &:hover {
      cursor: pointer;
    }
    :deep(.popover-label) {
      @media screen and (max-width: 1200px) {
        display: none;
      }
    }
    :deep(.popover-content.open) {
      &:hover {
        cursor: default;
      }
      @media screen and (max-width: 1200px) {
        top: 32px;
      }
      .help {
        @media screen and (max-width: 1200px) {
            width: 209px;
          }
        }
      }
      @media screen and (max-width: 1200px) {
        height: 40px;
        width: 40px;
        position: relative;
        padding: 0 !important;
        margin: 0 !important;
        min-height: 0px;
        bottom: 32px;
        left: 9px;
      }
      margin-right: 14px;
      float: right;
    }
    
  .branding-container {
    @media screen and (min-width: 1025px) {
        display: none;
    }
  }
  }

  .help-box-mobile-icon {
    display: none;
    @media screen and (max-width: 1200px) {
      display: inline-block;
    }
  }

  .help-box-container {
    @media screen and (max-width: 1200px) {
      width: 22px;
      height: 22px;
      position: relative;
      top: 7px;
      right: 5px;
      float: right;
  }
}

.top-container {
  @media screen and (max-width: 768px) {
    position: relative !important;
    left: 20px !important;
  }
  @media screen and (min-width:1024) and (max-width: 1200px) {
    position: relative;
    left: 60px;
  }
}

.reg-flow-footer {
  display: none;
  @media screen and (max-width: 1024px) {
    display: block;
  }
  :deep(.footer-label:nth-of-type(2)) {
    display: none;
  }
  :deep(.footer-content) {
    @media screen and (max-width: 768px) {
      padding: 30px 32px 30px 20px;
      justify-content: space-between !important;
    }
  }
}
.branding-container {

  img {
    max-height: 100px;
    max-width: 200px;
  }
}
.flexbox {
  width: 100%;
  height: 100%;
  display: flex;
  line-height: 1;
  flex-direction: none;
  flex: 1;
}
.container {
  flex-direction: column;
  @media screen and (max-width: 768px) {
    height: auto;
    min-height: calc(100% - 100px);
    overflow-y: auto;
 }
}
.content {
  flex-direction: column;
  padding: 15px 16.666666666666668%;
  box-sizing: border-box;
  @media screen and (max-width: 768px) {
      padding: 15px 5%;
  };

  @media screen and (max-width: 1400px) {
      padding: 15px 20px;
  };

  @media screen and (max-width: 1600px) {
      padding: 15px;
  };
}
.left-panel {
  flex-direction: column;
  height: 100%;
  color: #fff;
  @media only screen and (min-width: 1025px) {
    display: flex;
    flex: 1;
  }

  @media only screen and (max-width: 1024px) {
    display: none;
  }
}
</style>
